import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { formatCurrency, formatPriceLabel } from '@/core/utils/currency';
import { descriptorTitle } from '@/features/rapidstor/helpers';

import { DescriptorSwitchConfirmProps } from '../OptionSelector';

import classes from './DealDrawer.module.scss';

function useIsMobile() {
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return isMobile;
}

export default function DealDrawerConfirm({
    pendingSwitch,
    onConfirm,
    onCancel,
    location,
}: DescriptorSwitchConfirmProps) {
    const { t } = useTranslation();
    const isMobile = useIsMobile();

    const { descriptor, unitType, matchedDeal } = pendingSwitch;
    const dealCarriesOver = matchedDeal !== null;

    const displayRate = dealCarriesOver
        ? (matchedDeal.rates.discountedRate ?? matchedDeal.rates.rate)
        : unitType.rates.rate;

    const drawerContent = (
        <div className={classes.confirmContent}>
            <span className={classes.drawerTitle}>
                {t(
                    'deal_drawer_confirm:switch_title',
                    'Switch to a different size?',
                )}
            </span>

            <div className={classes.previewCard}>
                <div className={classes.descriptorName}>
                    {descriptorTitle(descriptor, location)[1]}
                </div>
                <div className={classes.unitTypeRate}>
                    {formatPriceLabel(formatCurrency(displayRate, location), location)}
                </div>
                <span
                    className={`${classes.dealChip} ${
                        dealCarriesOver
                            ? classes.dealCarriesOver
                            : classes.dealNotAvailable
                    }`}
                >
                    {dealCarriesOver
                        ? t(
                            'deal_drawer_confirm:deal_carries_over',
                            'Deal carries over',
                        )
                        : t(
                            'deal_drawer_confirm:deal_not_available',
                            'Deal not available - standard rate applied',
                        )}
                </span>
            </div>

            <div className={classes.divider} />

            <div className={classes.confirmButtons}>
                <button
                    type="button"
                    className={classes.cancelButton}
                    onClick={onCancel}
                >
                    {t('deal_drawer_confirm:cancel', 'Cancel')}
                </button>
                <button
                    type="button"
                    className={classes.confirmButton}
                    onClick={onConfirm}
                >
                    {t(
                        'deal_drawer_confirm:confirm_switch',
                        'Confirm & Switch',
                    )}
                </button>
            </div>
        </div>
    );

    const overlay = (
        <AnimatePresence>
            <motion.div
                className={classes.backdrop}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={onCancel}
            />

            {isMobile ? (
                <motion.div
                    className={classes.bottomSheet}
                    initial={{ y: '100%' }}
                    animate={{ y: 0 }}
                    exit={{ y: '100%' }}
                    transition={{
                        type: 'spring',
                        damping: 30,
                        stiffness: 300,
                    }}
                >
                    <div className={classes.dragHandle} />
                    {drawerContent}
                </motion.div>
            ) : (
                <motion.div
                    className={classes.sidePanel}
                    initial={{ x: '100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '100%' }}
                    transition={{
                        type: 'spring',
                        damping: 30,
                        stiffness: 300,
                    }}
                >
                    <button
                        type="button"
                        className={classes.closeButton}
                        onClick={onCancel}
                    >
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                    {drawerContent}
                </motion.div>
            )}
        </AnimatePresence>
    );

    return createPortal(overlay, document.body);
}
